import React, { Component } from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import FontAwesome from 'react-fontawesome';
import { DragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import cn from 'classnames';
import { STICKER_DROP } from '~/Config/DragTypes';
import { changeStarted, changeEnded } from '~/Redux/Modules/DroppedStickers';
import ScaleIcon from '../Assets/Svgs/ScaleIcon';
import RotateIcon from '../Assets/Svgs/RotateIcon';

import './StickerDrag.scss';

const MAX_SCALE = 5;
const MIN_SCALE = 0.5;

const eventDrag = {
  beginDrag: ({ sticker }, test, component) => {
    return {
      stickerRef: component.stickerRef,
      sticker: sticker,
    };
  },
};

const dragCollect = (connect, monitor) => {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
  };
};

@DragSource(STICKER_DROP, eventDrag, dragCollect)
class StickerDrag extends Component {
  constructor(props) {
    super(props);
    this.stickerRef = false;
    this.state = { scale: this.props.sticker.scale, updatedScale: 1 };
  }

  updateLocalScale = scale => {
    this.setState({ updatedScale: scale });
  };
  updateScaleState = newScale => {
    this.setState({ scale: newScale });
  };

  componentWillReceiveProps = nextProps => {
    this.updateScaleState(nextProps.sticker.scale);
  };

  componentDidMount() {
    //account for new stickers
    if (!this.props.sticker.scale) {
      this.props.sticker.scale = 1;
    }
    if (!this.props.sticker.rotation) {
      this.props.sticker.rotation = 0;
    }
    if (this.props.sticker) {
      const { connectDragPreview } = this.props;
      connectDragPreview(getEmptyImage());
    }
  }

  resizeSticker = (e, originalSticker, onResize) => {
    const { onChangeStart, onChangeEnd } = this.props;
    onChangeStart();
    const container = document.getElementById(`sticker-container-${originalSticker.id}`);
    const rect = container.getBoundingClientRect();
    const centerX = rect.left + (rect.right - rect.left) / 2;
    const centerY = rect.top + (rect.bottom - rect.top) / 2;

    let prevX = e.clientX;
    let prevY = e.clientY;
    const updateLocalScale = this.updateLocalScale;
    window.addEventListener('mousemove', mousemove);
    window.addEventListener('mouseup', mouseup);

    const startRadius = Math.sqrt(Math.pow(prevX - centerX, 2) + Math.pow(prevY - centerY, 2));

    function mousemove(e) {
      const radius = Math.sqrt(Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2));
      const newScale = radius / startRadius;
      const clampedScale = Math.min(
        Math.max(newScale * originalSticker.scale, MIN_SCALE),
        MAX_SCALE
      );
      updateLocalScale(clampedScale / originalSticker.scale);
    }

    function mouseup(e) {
      setTimeout(() => onChangeEnd());
      e.stopPropagation();
      e.preventDefault();
      onResize();
      window.removeEventListener('mousemove', mousemove);
      window.removeEventListener('mouseup', mouseup);
    }
  };

  handleResize = () => {
    const { resizeSticker, sticker } = this.props;
    const { scale, updatedScale } = this.state;
    const effectiveScale = (scale || 1) * (updatedScale || 1);
    resizeSticker({ ...sticker, scale: effectiveScale }, sticker.date);
    this.updateScaleState(effectiveScale);
    this.updateLocalScale(1);
  };

  render() {
    const {
      isDragging,
      connectDragSource,
      sticker,
      handleDelete,
      showDelete,
      rotateSticker,
    } = this.props;
    const { scale, updatedScale } = this.state;
    const effectiveScale = (scale || 1) * (updatedScale || 1);

    let style = {
      transform: `scale(${effectiveScale})`,
    };

    let useCenterX = false,
      useCenterY = false;
    if (sticker.position_version > 1) {
      if (Math.abs(sticker.position_right) < Math.abs(sticker.position_left))
        style = { ...style, right: sticker.position_right + '%' };
      if (Math.abs(sticker.position_bottom) < Math.abs(sticker.position_top))
        style = { ...style, bottom: sticker.position_bottom + '%' };
    }
    if (sticker.position_version > 2) {
      if (
        Math.abs(sticker.center_x) <
        Math.min(Math.abs(sticker.position_left), Math.abs(sticker.position_right)) * 5
      ) {
        style = {
          ...style,
          left: sticker.center_x + 50 + '%',
          transform: `translateX(-50%) ${style.transform}`,
          right: null,
        };
        useCenterX = true;
      }
      if (
        Math.abs(sticker.center_y) <
        Math.min(Math.abs(sticker.position_top), Math.abs(sticker.position_bottom)) * 5
      ) {
        style = {
          ...style,
          top: sticker.center_y + 50 + '%',
          transform: `translateY(-50%) ${style.transform}`,
          bottom: null,
        };
        useCenterY = true;
      }
    }
    if (!style.right && !useCenterX) style = { ...style, left: sticker.position_left + '%' };
    if (!style.bottom && !useCenterY) style = { ...style, top: sticker.position_top + '%' };

    if (sticker.position_version > 1) {
      style = {
        ...style,
        transformOrigin: cn({
          left: style.left && !useCenterX,
          right: style.right && !useCenterX,
          center: useCenterX,
          top: style.top && !useCenterY,
          bottom: style.bottom && !useCenterY,
          '50%': useCenterY,
        }),
      };
    }

    return connectDragSource(
      <div
        className={cn('sticker', { dragging: isDragging, version1: sticker.position_version == 1 })}
        style={style}
        id={`sticker-container-${sticker.id}`}
      >
        <img
          ref={r => (this.stickerRef = r)}
          src={sticker.url}
          className={`sticker-image`}
          id={`sticker-${sticker.id}`}
          style={{ transform: `rotate(${sticker.rotation}deg) ` }}
        />
        {showDelete && (
          <div className="sticker-actions" style={{ transform: `rotate(${sticker.rotation}deg) ` }}>
            <FontAwesome
              onClick={handleDelete}
              className="sticker-close"
              name="trash-o"
              style={{
                transform: `scale(${1 /
                  effectiveScale}) translate(50%, -50%) rotate(${-sticker.rotation}deg)`,
              }}
            />

            <span
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                rotateSticker(e, sticker);
              }}
              onClick={e => e.stopPropagation()}
              className="sticker-rotate"
              name="rotate-right"
              style={{
                transform: `scale(${1 /
                  effectiveScale}) translate(-50%, 50%) rotate(45deg) rotate(${-sticker.rotation}deg)`,
              }}
              id={`icon-${sticker.id}`}
            >
              <RotateIcon />
            </span>

            <span
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                this.resizeSticker(e, sticker, this.handleResize);
              }}
              onClick={e => e.stopPropagation()}
              className="sticker-resize"
              id={`resize-${sticker.id}`}
              name="expand"
              style={{
                transform: `scale(${1 / effectiveScale}) translate(50%, 50%) rotate(90deg) `,
              }}
            >
              <ScaleIcon />
            </span>
          </div>
        )}
      </div>
    );
  }
}

StickerDrag.propTypes = {
  sticker: propTypes.object,
  connectDragSource: propTypes.func,
  connectDragPreview: propTypes.func,
  handleDelete: propTypes.func,
  showDelete: propTypes.bool,
  isDragging: propTypes.bool,
  rotateSticker: propTypes.func,
  resizeSticker: propTypes.func,
  onChangeStart: propTypes.func.isRequired,
  onChangeEnd: propTypes.func.isRequired,
};

StickerDrag.defaultProps = {
  showDelete: true,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
  onChangeStart: () => dispatch(changeStarted()),
  onChangeEnd: () => dispatch(changeEnded()),
});

export default StickerDrag |> connect(mapStateToProps, mapDispatchToProps);
